import { useEffect, useState, useContext, createContext } from "react";
import { auth, db, signInWithGoogle as firebaseSignInWithGoogle } from "../firebase";
import { doc, getDoc, setDoc, serverTimestamp, collection, query, where, getDocs, updateDoc, deleteDoc } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';

/**
 * Unified Collection Structure:
 * - users: Single collection for all user accounts (visitor, host, appadmin)
 *   - Contains authentication info, role, and all user-specific data
 *   - Document ID: user.uid
 *   - Host fields: hostId (same as uid), officeResidence
 *   - Visitor fields: visitorId (same as uid)
 *   - Common fields: email, displayName, role, createdAt, updatedAt
 *
 * - visit_approvals: Records of visit requests
 *   - visitorId: References users.uid where role="visitor"
 *   - hostId: References users.uid where role="host"
 *   - Contains visitor information and approval status
 *
 * Role System:
 * - SuperAdmin: Identified by email in .env file (REACT_APP_SUPERADMIN_EMAIL1, etc.)
 *   - Access to host management and user management across all hosts
 * - AppAdmin: Role-based (role: 'appadmin') assigned through system
 *   - Access to user management for their specific host only
 * - Host: Standard host role (role: 'host', backward compatible with 'tenant')
 *   - hostId field = uid, contains officeResidence
 * - Visitor: Standard visitor role (role: 'visitor')
 *   - visitorId field = uid
 */

const AuthContext = createContext();

// Keep this export consistent - don't modify its structure
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
            
      if (firebaseUser) {
        try {
          const userDocRef = doc(db, 'users', firebaseUser.uid);
          const userDoc = await getDoc(userDocRef);
          
          if (userDoc.exists()) {
            const userData = userDoc.data();
            // Combine Firebase user with Firestore data using simplified structure

            setUser({
              uid: firebaseUser.uid,
              userID: userData.userID || firebaseUser.uid,
              userName: userData.userName || firebaseUser.displayName,
              userEmailID: userData.userEmailID || firebaseUser.email,
              userMobileNo: userData.userMobileNo || null,
              companyName: userData.companyName || userData.tenantName || null,
              tenantId: userData.tenantId || null,
              facilityId: userData.facilityId || null, 
              photoURL: firebaseUser.photoURL,
              role: userData.role || 'visitor'
            });
            setIsAuthenticated(true);
          } else {
                        // Try to find user by email for appoperator (or any admin-assigned user)
            const emailQuery = query(
              collection(db, 'users'),
              where('userEmailID', '==', firebaseUser.email)
            );
            console.log(firebaseUser.email);
            const emailSnapshot = await getDocs(emailQuery);
            if (!emailSnapshot.empty) {
              const foundDoc = emailSnapshot.docs[0];
              const foundData = foundDoc.data();
                            // If the found document's ID does not match the Auth UID, migrate it
              if (foundDoc.id !== firebaseUser.uid) {
                // Copy data to UID-based document
                // Ensure all relevant fields are copied and status is set to 'active' if pending_activation
                const migratedFields = {
                  ...foundData,
                  userID: firebaseUser.uid,
                  userEmailID: firebaseUser.email,
                  email: firebaseUser.email,
                  updatedAt: serverTimestamp(),
                  status: foundData.status === 'pending_activation' ? 'active' : foundData.status || 'active',
                };
                await setDoc(doc(db, 'users', firebaseUser.uid), migratedFields);
                // Delete the old document
                await deleteDoc(doc(db, 'users', foundDoc.id));
                                // Refresh user data after migration
                const migratedDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
                const migratedData = migratedDoc.exists() ? migratedDoc.data() : migratedFields;
                setUser({
                  uid: firebaseUser.uid,
                  userID: migratedData.userID || firebaseUser.uid,
                  userName: migratedData.userName || firebaseUser.displayName,
                  userEmailID: migratedData.userEmailID || firebaseUser.email,
                  userMobileNo: migratedData.userMobileNo || null,
                  companyName: migratedData.companyName || migratedData.tenantName || null,
                  tenantId: migratedData.tenantId || null,
                  photoURL: firebaseUser.photoURL,
                  role: migratedData.role || 'visitor',
                  status: migratedData.status || 'active',
                });
                setIsAuthenticated(true);
                setLoading(false);
                return;
              }
            } // <-- CLOSE if (!emailSnapshot.empty)

            // Check if this user is a SuperAdmin FIRST
            const superAdminEmails = [];
            for (let i = 1; i <= 10; i++) {
              const emailKey = `VITE_SUPERADMIN_EMAIL${i}`;
              const email = import.meta.env[emailKey];
              if (email) {
                superAdminEmails.push(email.trim());
              }
            }
            // Fallback for development
            if (superAdminEmails.length === 0) {
              superAdminEmails.push('admin@example.com', 'aryanchaudhari44@gmail.com');
            }

            const isSuperAdminUser = superAdminEmails.includes(firebaseUser.email);
            
            if (isSuperAdminUser) {
              // Create SuperAdmin account
              
              setUser({
                uid: firebaseUser.uid,
                userID: firebaseUser.uid,
                userName: firebaseUser.displayName || 'SuperAdmin',
                userEmailID: firebaseUser.email,
                userMobileNo: null,
                companyName: null,
                photoURL: firebaseUser.photoURL,
                role: 'superadmin'
              });

              // Create SuperAdmin user document
              try {
                await setDoc(userDocRef, {
                  userID: firebaseUser.uid,
                  userName: firebaseUser.displayName || 'SuperAdmin',
                  userEmailID: firebaseUser.email,
                  userMobileNo: null,
                  companyName: null,
                  role: 'superadmin',
                  createdAt: serverTimestamp()
                });

                              } catch (docError) {
                console.error("Error creating SuperAdmin document:", docError);
                // Continue anyway - the user object is already set
              }
            } else {
              // Check if this user is a pre-registered host by email
              const preRegisteredQuery = query(
                collection(db, 'users'),
                where('userEmailID', '==', firebaseUser.email),
                where('status', '==', 'pending_activation')
              );

              try {
                const preRegisteredSnapshot = await getDocs(preRegisteredQuery);

                if (!preRegisteredSnapshot.empty) {
                  // Found a pre-registered host account
                  const preRegisteredDoc = preRegisteredSnapshot.docs[0];
                  const preRegisteredData = preRegisteredDoc.data();

                  
                  // Create new document with Firebase Auth UID as document ID
                  await setDoc(doc(db, 'users', firebaseUser.uid), {
                    ...preRegisteredData,
                    userID: firebaseUser.uid,
                    status: 'active',
                    activatedAt: serverTimestamp()
                  });

                  // Delete the temporary document
                  await deleteDoc(doc(db, 'users', preRegisteredDoc.id));

                  // Set user with host role
                  setUser({
                    uid: firebaseUser.uid,
                    userID: firebaseUser.uid,
                    userName: preRegisteredData.userName,
                    userEmailID: preRegisteredData.userEmailID,
                    userMobileNo: preRegisteredData.userMobileNo,
                    companyName: preRegisteredData.companyName || preRegisteredData.tenantName,
                    photoURL: firebaseUser.photoURL,
                    role: preRegisteredData.role
                  });

                                  } else {
                  // Create visitor account
                  
                  setUser({
                    uid: firebaseUser.uid,
                    userID: firebaseUser.uid,
                    userName: firebaseUser.displayName || 'New User',
                    userEmailID: firebaseUser.email,
                    userMobileNo: null,
                    companyName: null,
                    photoURL: firebaseUser.photoURL,
                    role: 'visitor'
                  });

                  // Create visitor user document
                  await setDoc(userDocRef, {
                    userID: firebaseUser.uid,
                    userName: firebaseUser.displayName || 'New User',
                    userEmailID: firebaseUser.email,
                    userMobileNo: null,
                    companyName: null,
                    role: 'visitor',
                    createdAt: serverTimestamp()
                  });

                                  }
              } catch (queryError) {
                console.error("Error checking for pre-registered account:", queryError);
                // Fallback to creating visitor account
                setUser({
                  uid: firebaseUser.uid,
                  userID: firebaseUser.uid,
                  userName: firebaseUser.displayName || 'New User',
                  userEmailID: firebaseUser.email,
                  userMobileNo: null,
                  companyName: null,
                  photoURL: firebaseUser.photoURL,
                  role: 'visitor'
                });
              }
            }

            setIsAuthenticated(true);
          }
        } catch (error) {
          console.error("Error fetching user data:", error);
          // Still set basic user info so they can use the app
          setUser({
            uid: firebaseUser.uid,
            userID: firebaseUser.uid,
            userName: firebaseUser.displayName || 'User',
            userEmailID: firebaseUser.email,
            userMobileNo: null,
            companyName: null,
            photoURL: firebaseUser.photoURL,
            role: 'visitor' // Default role
          });
          setIsAuthenticated(true);
        }
      } else {
        // No user signed in
        setUser(null);
        setIsAuthenticated(false);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const loginWithGoogle = async (isSignup = false) => {
    try {
      const result = await firebaseSignInWithGoogle();

      // If this is a login (not signup) attempt, check if user exists
      if (!isSignup && result.user) {
        // Check if this user is a SuperAdmin FIRST
        const superAdminEmails = [];
        for (let i = 1; i <= 10; i++) {
          const emailKey = `VITE_SUPERADMIN_EMAIL${i}`;
          const email = import.meta.env[emailKey];
          if (email) {
            superAdminEmails.push(email.trim());
          }
        }
        // Fallback for development
        if (superAdminEmails.length === 0) {
          superAdminEmails.push('admin@example.com', 'aryanchaudhari44@gmail.com');
        }

        const isSuperAdminUser = superAdminEmails.includes(result.user.email);

        if (isSuperAdminUser) {
                    return result; // Allow SuperAdmin to login even without existing document
        }

        const userDocRef = doc(db, 'users', result.user.uid);
        const userDoc = await getDoc(userDocRef);

        // If user document doesn't exist, check for pre-registered host
        if (!userDoc.exists()) {
          // Check if this user is a pre-registered host by email
          const preRegisteredQuery = query(
            collection(db, 'users'),
            where('userEmailID', '==', result.user.email),
            where('status', '==', 'pending_activation')
          );

          const preRegisteredSnapshot = await getDocs(preRegisteredQuery);

          if (!preRegisteredSnapshot.empty) {
            // Found a pre-registered host account - activate it
            const preRegisteredDoc = preRegisteredSnapshot.docs[0];
            const preRegisteredData = preRegisteredDoc.data();

            
            // Create new document with Firebase Auth UID as document ID
            await setDoc(doc(db, 'users', result.user.uid), {
              ...preRegisteredData,
              userID: result.user.uid,
              status: 'active',
              activatedAt: serverTimestamp()
            });

            // Delete the temporary document
            await deleteDoc(doc(db, 'users', preRegisteredDoc.id));

                        return result; // Allow login to proceed
          } else {
            // No pre-registered account found
            await auth.signOut(); // Sign out the user
            throw new Error('No account found with these credentials. Please sign up first or contact your administrator.');
          }
        }
      }

      return result;
    } catch (error) {
      console.error("Google Sign-In Error:", error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await auth.signOut();
      setUser(null);
      localStorage.clear();
      sessionStorage.clear();
    } catch (error) {
      console.error("Logout Error:", error);
      throw error;
    }
  };

  // Add function to update user role
  const updateUserRole = async (uid, newRole) => {
    try {
      const userDocRef = doc(db, 'users', uid);
      await setDoc(userDocRef, { role: newRole }, { merge: true });

      // Update local user state
      if (user && user.uid === uid) {
        setUser({
          ...user,
          role: newRole
        });
      }

      return true;
    } catch (error) {
      console.error("Error updating user role:", error);
      throw error;
    }
  };

  // Add function to refresh user data from database
  const refreshUserData = async () => {
    if (!auth.currentUser) return;

    try {
      const userDocRef = doc(db, 'users', auth.currentUser.uid);
      const userDoc = await getDoc(userDocRef);

      if (userDoc.exists()) {
        const userData = userDoc.data();
        setUser({
          uid: auth.currentUser.uid,
          userID: userData.userID || auth.currentUser.uid,
          userName: userData.userName || auth.currentUser.displayName,
          userEmailID: userData.userEmailID || auth.currentUser.email,
          userMobileNo: userData.userMobileNo || null,
          tenantName: userData.tenantName || null,
          photoURL: auth.currentUser.photoURL,
          role: userData.role || 'visitor'
        });
              }
    } catch (error) {
      console.error("Error refreshing user data:", error);
    }
  };

  // Super admin check function - checks both role and email
  const isSuperAdmin = () => {
    if (!user) return false;

    // First check if user has superadmin role
    if (user.role === 'superadmin') {
      return true;
    }

    // Fallback: check email against environment variables
    const superAdminEmails = [];
    for (let i = 1; i <= 10; i++) {
      const emailKey = `VITE_SUPERADMIN_EMAIL${i}`;
      const email = import.meta.env[emailKey];
      if (email) {
        superAdminEmails.push(email.trim());
      }
    }

    // Fallback for development if no env vars are set
    if (superAdminEmails.length === 0) {
      superAdminEmails.push('admin@example.com', 'aryanchaudhari44@gmail.com');
    }

    return superAdminEmails.includes(user.userEmailID || user.email);
  };

  // App admin check function
  const isAppAdmin = () => {
    return user && user.role === 'appadmin';
  };

  const isFacilityAdmin = () => {
    return user?.role === 'facilityadmin';
  };

  // Check if user has admin privileges (either super admin or app admin)
  const hasAdminPrivileges = () => {
    return isSuperAdmin() || isAppAdmin();
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    loginWithGoogle,
    logout,
    updateUserRole,
    refreshUserData,
    isSuperAdmin,
    isAppAdmin,
    isFacilityAdmin,
    hasAdminPrivileges
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
